import React from "react";
import {Button, MenuItem, Select} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCloudUploadAlt} from "@fortawesome/pro-regular-svg-icons";

import Dropzone from "js/common/views/inputs/dropzone";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {uploadDashboardBackgroundImage} from "js/dashboards/settings/upload-dashboard-background";
import {validateBackgroundImageFile} from "js/dashboards/settings/appearance-utils";

const dropzoneInnerStyle = (theme) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  border: `1px dashed ${theme.palette.border.light}`,
  borderRadius: 6,
  textAlign: "center",
  cursor: "pointer"
});

// Reusable editor field for a panel background image. `value` is `{url, fit}` or null,
// where `fit` is "cover" | "contain". Reuses the dashboard-background upload workflow.
const PanelBackgroundImageField = React.memo(({value, onChange}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);

  const handleDrop = (files) => {
    const file = files[0];
    if (!file) {
      return;
    }
    const {valid, error} = validateBackgroundImageFile(file);
    if (!valid) {
      setUploadError(error);
      return;
    }
    setUploadError(null);
    setIsUploading(true);
    return uploadDashboardBackgroundImage(file)
      .then(url => onChange({url, fit: (value && value.fit) || "cover"}))
      .catch(() => setUploadError("Upload failed. Please try again."))
      .finally(() => setIsUploading(false));
  };

  if (value && value.url) {
    return <div>
      <img data-test-id="panel-image-preview" src={value.url} alt="background preview"
           style={{width: "100%", maxHeight: 160, objectFit: value.fit || "cover", borderRadius: 4,
                   border: `1px solid ${theme.palette.border.light}`}} />
      <div style={{marginTop: 12}}>
        <label style={{color: theme.palette.text.main, fontSize: 13}}>Sizing</label>
        <Select size="small" fullWidth data-test-id="panel-image-fit-select"
                value={value.fit || "cover"}
                onChange={(e) => onChange({...value, fit: e.target.value})}>
          {/* Values stay cover/contain (they map straight to CSS background-size); only the
              user-facing labels change to the plainer Fill/Fit wording. */}
          <MenuItem value="cover">Fill</MenuItem>
          <MenuItem value="contain">Fit</MenuItem>
        </Select>
      </div>
      <Button size="small" color="error" variant="text" data-test-id="panel-image-remove"
              style={{marginTop: 8,
                      backgroundColor: theme.themeId === "dark" ? theme.palette.background.card : undefined}}
              onClick={() => onChange(null)}>Remove image</Button>
    </div>;
  }

  return <div>
    <Dropzone multiple={false}
              accept={{"image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/gif": [".gif"]}}
              onDrop={handleDrop} className="react-dropzone">
      <div style={dropzoneInnerStyle(theme)}>
        <FontAwesomeIcon icon={faCloudUploadAlt}
                         style={{fontSize: 24, color: theme.palette.text.secondary, marginBottom: 6}} />
        <div style={{fontSize: 12, fontWeight: 600, color: theme.palette.text.main}}>
          {isUploading ? "Uploading…" : "Drop an image or click to upload"}
        </div>
        <div style={{fontSize: 11, color: theme.palette.text.secondary, marginTop: 2}}>
          PNG, JPEG or GIF, up to 10MB
        </div>
      </div>
    </Dropzone>
    {uploadError && <div data-test-id="panel-image-error"
                         style={{color: theme.palette.error.main, fontSize: 12, marginTop: 8}}>{uploadError}</div>}
  </div>;
});

export default PanelBackgroundImageField;
